import { format } from 'date-fns'
import moment from 'moment-timezone'
import {
  WaveScheduleCalendarEntry,
  WaveSchedule,
  WaveScheduleBlackoutDate,
  WaveScheduleDownstreamStatus,
  WaveScheduleDraft,
  WaveScheduleOccurrenceStatus,
  WaveScheduleOccurrence,
  WaveScheduleTarget,
  WaveScheduleTargetOption,
  WaveScheduleTargetType,
} from './schedule-types'
import { ScheduleCalendarEntry } from '../../../../components/component-items/schedule-calendar'
import { normalizeScheduleTimeValue } from '../../../../components/component-items/schedule-time'

export type ScheduleTimeDisplayMode = 'warehouse' | 'viewer'

const ISO_OFFSET_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/i
export const WAVE_SCHEDULE_LIST_LIMIT = 1000

export const buildWaveScheduleListUrl = (startDate: string, endDate: string) => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    limit: String(WAVE_SCHEDULE_LIST_LIMIT),
  })
  return `/api/inventory/wave-schedules/?${params.toString()}`
}

export const removeManualSkipOccurrence = (occurrences: WaveScheduleOccurrence[], scheduledAt: string) => {
  const unskippedTime = new Date(scheduledAt).getTime()
  return occurrences.filter(
    (occurrence) =>
      occurrence.status !== 'skipped_manual' || new Date(occurrence.scheduled_at).getTime() !== unskippedTime
  )
}

export const getViewerTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

export const getDisplayTimezone = (
  warehouseTimezone: string,
  displayMode: ScheduleTimeDisplayMode,
  viewerTimezone = getViewerTimezone()
) => (displayMode === 'warehouse' ? warehouseTimezone : viewerTimezone)

const parseValueInTimezone = (value: string, timezone: string) => {
  if (!value) return null
  return ISO_OFFSET_PATTERN.test(value) ? moment(value).tz(timezone) : moment.tz(value, timezone)
}

export const parseDateTimeInTimezone = (value: string, timezone: string) => parseValueInTimezone(value, timezone)

const toMomentDate = (date: Date, timezone: string) => moment(date).tz(timezone)

const normalizeExecutionTime = (timeOfDay: string) => normalizeScheduleTimeValue(timeOfDay)

const buildExecutionMoment = (dateKey: string, timeOfDay: string, timezone: string) =>
  moment.tz(`${dateKey} ${normalizeExecutionTime(timeOfDay)}`, 'YYYY-MM-DD HH:mm:ss', timezone)

const weekdayFromMoment = (dateMoment: moment.Moment) => {
  const day = dateMoment.day()
  return day === 0 ? 7 : day
}

const isBlackoutDate = (dateKey: string, blackoutDates: WaveScheduleBlackoutDate[]) =>
  blackoutDates.some(({ date: blackoutDate }) => blackoutDate === dateKey)

export const weekdayOptions = [
  { value: 1, shortLabel: 'Mon', longLabel: 'Monday' },
  { value: 2, shortLabel: 'Tue', longLabel: 'Tuesday' },
  { value: 3, shortLabel: 'Wed', longLabel: 'Wednesday' },
  { value: 4, shortLabel: 'Thu', longLabel: 'Thursday' },
  { value: 5, shortLabel: 'Fri', longLabel: 'Friday' },
  { value: 6, shortLabel: 'Sat', longLabel: 'Saturday' },
  { value: 7, shortLabel: 'Sun', longLabel: 'Sunday' },
]

export const scheduleStatusConfig: Record<
  WaveScheduleOccurrenceStatus,
  { label: string; tone: 'silver' | 'blue' | 'yellow' | 'green' | 'red'; detail: string }
> = {
  pending: {
    label: 'Pending',
    tone: 'silver',
    detail: 'Scheduled and waiting for its execution window.',
  },
  launching: {
    label: 'Launching',
    tone: 'blue',
    detail: 'The scheduler is dispatching the run now.',
  },
  launched: {
    label: 'Started',
    tone: 'green',
    detail: 'The scheduler started the run successfully.',
  },
  failed_to_launch: {
    label: 'Failed',
    tone: 'red',
    detail: 'Launch failed before the run started.',
  },
  skipped_blackout: {
    label: 'Blackout',
    tone: 'silver',
    detail: 'This warehouse-local slot was intentionally skipped by a configured blackout date.',
  },
  skipped_downtime: {
    label: 'Skipped',
    tone: 'silver',
    detail: 'Downtime',
  },
  skipped_manual: {
    label: 'Skipped Manually',
    tone: 'silver',
    detail: 'This slot was manually skipped by an operator.',
  },
}

const isWithinScheduleWindow = (dateKey: string, startDate?: string | null, endDate?: string | null) => {
  if (startDate && dateKey < startDate) return false
  if (endDate && dateKey > endDate) return false
  return true
}

const sundayStartOfWeek = (value: moment.Moment) => {
  const dayOfWeek = value.day()
  return value.clone().startOf('day').subtract(dayOfWeek, 'days')
}

const isDueOnDate = (
  schedule: {
    schedule_type: WaveScheduleDraft['schedule_type']
    recurrence_interval?: number
    days_of_week: number[] | null
    date_rule: WaveScheduleDraft['date_rule']
    day_of_month: number | null
    week_segment: WaveScheduleDraft['week_segment']
    day_of_week: number | null
    start_date?: string | null
    end_date?: string | null
  },
  dateMoment: moment.Moment,
  dateKey: string
) => {
  if (!isWithinScheduleWindow(dateKey, schedule.start_date, schedule.end_date)) {
    return false
  }

  const recurrenceInterval = Math.max(1, Number(schedule.recurrence_interval || 1))
  if (recurrenceInterval > 1 && schedule.start_date) {
    const scheduleTimezone = dateMoment.tz() || 'UTC'
    const anchor = moment.tz(schedule.start_date, 'YYYY-MM-DD', scheduleTimezone)
    if (anchor.isValid()) {
      if (schedule.schedule_type === 'daily') {
        const dayDiff = dateMoment.clone().startOf('day').diff(anchor.clone().startOf('day'), 'days')
        if (dayDiff < 0 || dayDiff % recurrenceInterval !== 0) return false
      }

      if (schedule.schedule_type === 'weekly') {
        const anchorWeekStart = sundayStartOfWeek(anchor)
        const dateWeekStart = sundayStartOfWeek(dateMoment)
        const weekDiff = dateWeekStart.diff(anchorWeekStart, 'days') / 7
        if (weekDiff < 0 || weekDiff % recurrenceInterval !== 0) return false
      }

      if (schedule.schedule_type === 'monthly') {
        const monthDiff = (dateMoment.year() - anchor.year()) * 12 + (dateMoment.month() - anchor.month())
        if (monthDiff < 0 || monthDiff % recurrenceInterval !== 0) return false
      }
    }
  }

  if (schedule.schedule_type === 'daily') {
    return true
  }

  if (schedule.schedule_type === 'weekly') {
    return (schedule.days_of_week || []).includes(weekdayFromMoment(dateMoment))
  }

  if (schedule.date_rule === 'month_segment') {
    const segmentIndex = ['first', 'second', 'third', 'fourth'].indexOf(schedule.week_segment || 'first') + 1
    const targetWeekday = schedule.day_of_week || 1
    return weekdayFromMoment(dateMoment) === targetWeekday && Math.ceil(dateMoment.date() / 7) === segmentIndex
  }

  const targetDay = schedule.day_of_month || 1
  return dateMoment.date() === Math.min(targetDay, dateMoment.daysInMonth())
}

export const formatDateTime = (value?: string | null, includeWeekday = false, timezone?: string | null) => {
  if (!value) return 'Not scheduled'
  const targetTimezone = timezone || getViewerTimezone()
  const parsed = parseValueInTimezone(value, targetTimezone)
  if (!parsed || !parsed.isValid()) return 'Not scheduled'
  return parsed.format(includeWeekday ? 'ddd, MMM D, YYYY h:mm A' : 'MMM D, YYYY h:mm A')
}

export const formatTimeOfDay = (timeOfDay: string) => {
  const normalizedTime = normalizeExecutionTime(timeOfDay)
  const [hour, minute, second] = normalizedTime.split(':').map(Number)
  const sample = new Date(2026, 0, 1, hour, minute, second)
  return format(sample, 'h:mm a')
}

export const formatExecutionTimes = (executionTimes: string[]) => {
  if (!executionTimes?.length) return 'No times configured'
  return executionTimes.map(formatTimeOfDay).join(' and ')
}

export const formatRecurrence = (
  schedule: Pick<
    WaveSchedule,
    | 'schedule_type'
    | 'recurrence_interval'
    | 'execution_times'
    | 'days_of_week'
    | 'date_rule'
    | 'day_of_month'
    | 'week_segment'
    | 'day_of_week'
  >
) => {
  const timeLabel = formatExecutionTimes(schedule.execution_times)
  const interval = Math.max(1, Number(schedule.recurrence_interval || 1))
  const everyLabel = interval > 1 ? `Every ${interval}` : 'Every'
  if (schedule.schedule_type === 'daily') {
    return interval > 1 ? `${everyLabel} days at ${timeLabel}` : `Daily at ${timeLabel}`
  }
  if (schedule.schedule_type === 'weekly') {
    const days = weekdayOptions
      .filter(({ value }) => (schedule.days_of_week || []).includes(value))
      .map(({ shortLabel }) => shortLabel)
      .join(', ')
    const weeklyPrefix = interval > 1 ? `${everyLabel} weeks` : 'Every week'
    return `${weeklyPrefix} on ${days || 'selected day'} at ${timeLabel}`
  }
  if (schedule.date_rule === 'month_segment') {
    const weekday = weekdayOptions.find(({ value }) => value === schedule.day_of_week)?.longLabel || 'Monday'
    const monthlyPrefix = interval > 1 ? `${everyLabel} months` : 'Monthly'
    return `${monthlyPrefix} on the ${schedule.week_segment || 'first'} ${weekday} at ${timeLabel}`
  }
  const monthlyPrefix = interval > 1 ? `${everyLabel} months` : 'Monthly'
  return `${monthlyPrefix} on day ${schedule.day_of_month || 1} at ${timeLabel}`
}

export const summarizeTargets = (targets: WaveScheduleTarget[]) => {
  const profiles = targets.filter(({ target_type }) => target_type === 'wave_profile').length
  const directives = targets.filter(({ target_type }) => target_type === 'wave_directive').length
  const parts = []
  if (profiles) parts.push(`${profiles} profile${profiles === 1 ? '' : 's'}`)
  if (directives) parts.push(`${directives} directive${directives === 1 ? '' : 's'}`)
  return parts.join(' + ') || 'No targets'
}

export const orderedTargetLabel = (targets: WaveScheduleTarget[]) =>
  targets
    .slice()
    .sort((left, right) => left.execution_order - right.execution_order)
    .map((target) => `${target.execution_order}. ${target.label}`)
    .join(' -> ')

const MAX_PREVIEW_HORIZON_DAYS = 36500

const getPreviewHorizonDays = (draft: WaveScheduleDraft, count: number) => {
  const interval = Math.max(1, Number(draft.recurrence_interval || 1))
  const requestedRuns = Math.max(1, count)
  const cadenceDays = draft.schedule_type === 'daily' ? 1 : draft.schedule_type === 'weekly' ? 7 : 31
  return Math.min(interval * requestedRuns * cadenceDays + cadenceDays, MAX_PREVIEW_HORIZON_DAYS)
}

const sortedExecutionTimes = (executionTimes: string[]) =>
  [...(executionTimes || [])].sort((left, right) =>
    normalizeExecutionTime(left).localeCompare(normalizeExecutionTime(right))
  )

export const buildNextRuns = (
  draft: WaveScheduleDraft,
  count = 3,
  fromDate = new Date(),
  displayMode: ScheduleTimeDisplayMode = 'warehouse',
  viewerTimezone = getViewerTimezone()
) => {
  const entries: string[] = []
  const warehouseTimezone = draft.warehouse_timezone || 'America/Los_Angeles'
  const displayTimezone = getDisplayTimezone(warehouseTimezone, displayMode, viewerTimezone)
  const referenceNow = toMomentDate(fromDate, warehouseTimezone)
  let cursor = toMomentDate(fromDate, warehouseTimezone).startOf('day')
  let attempts = 0

  const horizonDays = getPreviewHorizonDays(draft, count)
  while (entries.length < count && attempts < horizonDays) {
    const dateKey = cursor.format('YYYY-MM-DD')
    if (isDueOnDate(draft, cursor, dateKey) && !isBlackoutDate(dateKey, draft.blackout_dates)) {
      sortedExecutionTimes(draft.execution_times).forEach((timeOfDay) => {
        const executionMoment = buildExecutionMoment(dateKey, timeOfDay, warehouseTimezone)
        if (entries.length < count && executionMoment.isAfter(referenceNow)) {
          entries.push(formatDateTime(executionMoment.toISOString(), true, displayTimezone))
        }
      })
    }
    cursor = cursor.clone().add(1, 'day')
    attempts += 1
  }

  return entries
}

const getNextRunIso = (draft: WaveScheduleDraft, fromDate = new Date()) => {
  const warehouseTimezone = draft.warehouse_timezone || 'America/Los_Angeles'
  const referenceNow = toMomentDate(fromDate, warehouseTimezone)
  let cursor = toMomentDate(fromDate, warehouseTimezone).startOf('day')
  let attempts = 0

  const horizonDays = getPreviewHorizonDays(draft, 1)
  while (attempts < horizonDays) {
    const dateKey = cursor.format('YYYY-MM-DD')
    if (isDueOnDate(draft, cursor, dateKey) && !isBlackoutDate(dateKey, draft.blackout_dates)) {
      const nextRun = sortedExecutionTimes(draft.execution_times)
        .map((timeOfDay) => buildExecutionMoment(dateKey, timeOfDay, warehouseTimezone))
        .find((executionMoment) => executionMoment.isAfter(referenceNow))
      if (nextRun) return nextRun.toISOString()
    }

    cursor = cursor.clone().add(1, 'day')
    attempts += 1
  }

  return null
}

export const buildCalendarEntries = (
  schedules: WaveSchedule[],
  rangeStart: Date,
  rangeEnd: Date,
  displayMode: ScheduleTimeDisplayMode = 'warehouse',
  viewerTimezone = getViewerTimezone(),
  estimatedOrderCountsByScheduleId: Record<number, number[] | null> = {},
  todayDateKey?: string
) => {
  const entries: ScheduleCalendarEntry[] = []
  const dateKeyInTimezone = (date: Date, timezone: string) => moment(date).tz(timezone).format('YYYY-MM-DD')
  const downstreamStatusTone = (status?: Exclude<WaveScheduleDownstreamStatus, null>) => {
    if (status === 'failed') return 'failed' as const
    if (status === 'running' || status === 'queued') return 'active' as const
    if (status === 'completed') return 'success' as const
    return null
  }
  const calendarEntryStatusTone = (
    entry: WaveScheduleCalendarEntry,
    schedule: WaveSchedule
  ): ScheduleCalendarEntry['statusTone'] => {
    if (entry.status === 'failed_to_launch') return 'failed'
    if (entry.status === 'skipped_blackout' || entry.status === 'skipped_manual') return 'blackout'
    if (entry.status === 'skipped_downtime') return 'attention'
    if (entry.status === 'pending' || entry.status === 'launching' || entry.status === 'scheduled') return 'active'
    if (entry.status === 'launched') {
      const downstreamTone = downstreamStatusTone(entry.downstream_run?.status)
      if (downstreamTone) return downstreamTone
    }
    return 'success'
  }
  const orderEstimateLabel = (scheduleId: number) => {
    const counts = estimatedOrderCountsByScheduleId[scheduleId]
    if (!counts?.length || counts.some((count) => !Number.isFinite(count) || count < 0)) return undefined

    const minimum = Math.max(...counts)
    const maximum = counts.reduce((total, count) => total + count, 0)
    return minimum === maximum ? `Est. ${minimum} orders` : `Est. ${minimum}-${maximum} orders`
  }

  schedules.forEach((schedule) => {
    const warehouseTimezone = schedule.warehouse_timezone || 'America/Los_Angeles'
    const displayTimezone = getDisplayTimezone(warehouseTimezone, displayMode, viewerTimezone)
    const effectiveTodayDateKey = todayDateKey || getTodayDateInTimezone(displayTimezone)
    const visibleStartKey = dateKeyInTimezone(rangeStart, displayTimezone)
    const visibleEndKey = dateKeyInTimezone(rangeEnd, displayTimezone)
    const firstUnrunEntry = (schedule.calendar_entries || [])
      .filter((entry) => {
        if (entry.status !== 'scheduled' && entry.status !== 'pending') return false
        const scheduledAt = parseValueInTimezone(entry.scheduled_at, warehouseTimezone)
        return (
          scheduledAt?.isValid() &&
          scheduledAt.clone().tz(displayTimezone).format('YYYY-MM-DD') === effectiveTodayDateKey
        )
      })
      .sort((left, right) => left.scheduled_at.localeCompare(right.scheduled_at))[0]
    ;(schedule.calendar_entries || []).forEach((calendarEntry) => {
      const scheduledAt = parseValueInTimezone(calendarEntry.scheduled_at, warehouseTimezone)
      if (!scheduledAt?.isValid()) return
      const displayMoment = scheduledAt.clone().tz(displayTimezone)
      const displayDateKey = displayMoment.format('YYYY-MM-DD')
      if (displayDateKey < visibleStartKey || displayDateKey > visibleEndKey) return
      const isUnrunToday =
        displayDateKey === effectiveTodayDateKey &&
        (calendarEntry.status === 'scheduled' || calendarEntry.status === 'pending')
      const isFirstUnrunToday = isUnrunToday && calendarEntry === firstUnrunEntry

      const statusTone = calendarEntryStatusTone(calendarEntry, schedule)
      const isRunningOccurrence =
        calendarEntry.status === 'launching' ||
        (calendarEntry.status === 'launched' &&
          (calendarEntry.downstream_run?.status === 'running' || calendarEntry.downstream_run?.status === 'queued'))

      entries.push({
        id: `${schedule.id}-${calendarEntry.source}-${calendarEntry.occurrence_id ?? calendarEntry.scheduled_at}`,
        sourceId: schedule.id,
        date: displayDateKey,
        kind:
          calendarEntry.status === 'skipped_blackout'
            ? 'blackout'
            : calendarEntry.status === 'skipped_manual'
              ? 'manual_skip'
              : isRunningOccurrence
                ? 'running'
                : 'scheduled',
        title: schedule.name,
        subtitle:
          calendarEntry.status === 'skipped_blackout'
            ? 'Skipped by blackout date'
            : calendarEntry.status === 'skipped_manual'
              ? 'Skipped manually'
              : summarizeTargets(schedule.targets),
        scheduledAt: scheduledAt.toISOString(),
        displayTimeLabel: displayMoment.format('h:mm A'),
        orderEstimateLabel: isFirstUnrunToday
          ? orderEstimateLabel(schedule.id)
          : isUnrunToday
            ? 'Estimate pending earlier run'
            : undefined,
        minutesFromMidnight: displayMoment.hours() * 60 + displayMoment.minutes(),
        statusTone,
      })
    })
  })

  return entries.sort((left, right) => left.scheduledAt.localeCompare(right.scheduledAt))
}

export const convertDisplaySlotToWarehouse = (
  slotDate: string,
  slotTime: string,
  displayTimezone: string,
  warehouseTimezone: string
) => {
  const displayMoment = moment.tz(
    `${slotDate} ${normalizeExecutionTime(slotTime)}`,
    'YYYY-MM-DD HH:mm:ss',
    displayTimezone
  )
  const warehouseMoment = displayMoment.clone().tz(warehouseTimezone)
  return {
    date: warehouseMoment.format('YYYY-MM-DD'),
    time: warehouseMoment.format('HH:mm:ss'),
  }
}

export const isWarehouseSlotInPast = (slotDate: string, slotTime: string, warehouseTimezone: string) => {
  const slotMoment = moment.tz(
    `${slotDate} ${normalizeExecutionTime(slotTime)}`,
    'YYYY-MM-DD HH:mm:ss',
    warehouseTimezone
  )
  if (!slotMoment.isValid()) return false
  return slotMoment.isBefore(moment.tz(warehouseTimezone))
}

export const getTodayDateInTimezone = (timezone: string) => moment.tz(timezone).format('YYYY-MM-DD')

export const getCurrentMinutesInTimezone = (timezone: string) => {
  const now = moment.tz(timezone)
  return now.hours() * 60 + now.minutes()
}

export const buildWaveScheduleTargetId = (targetType: WaveScheduleTargetType, targetId: number | string) =>
  `${targetType.replace(/_/g, '-')}-${targetId}`

export const normalizeTargetsFromProfiles = (profiles: any[]): WaveScheduleTargetOption[] => {
  const options: WaveScheduleTargetOption[] = []

  profiles.forEach((profile: any) => {
    const companyLabels = Array.isArray(profile.companies)
      ? profile.companies.map(({ label }: { label: string }) => label).filter(Boolean)
      : []
    const companyLabel = companyLabels.join(', ')
    const profileLabel = companyLabel || profile.name || profile.profile || `Wave Profile ${profile.id}`

    options.push({
      id: buildWaveScheduleTargetId('wave_profile', profile.id),
      target_type: 'wave_profile',
      execution_order: 1,
      label: profileLabel,
      secondary_label: 'Wave Profile',
      company_labels: companyLabels,
      wave_profile_id: Number(profile.id),
      wave_directive_id: null,
      parent_profile_id: Number(profile.id),
    })
    ;(profile.wave_directives || []).forEach((directive: any) => {
      options.push({
        id: buildWaveScheduleTargetId('wave_directive', directive.id),
        target_type: 'wave_directive',
        execution_order: 1,
        label: directive.name || `Wave Directive ${directive.id}`,
        secondary_label: profileLabel,
        company_labels: companyLabels,
        wave_profile_id: null,
        wave_directive_id: Number(directive.id),
        parent_profile_id: Number(profile.id),
      })
    })
  })

  return options
}

export const targetConflictIds = (selectedTargets: WaveScheduleTarget[]) => {
  const blocked = new Set<string>()

  selectedTargets.forEach((target) => {
    blocked.add(target.id)
    blocked.add(
      buildWaveScheduleTargetId(target.target_type, target.wave_profile_id || target.wave_directive_id || target.id)
    )

    if (target.target_type === 'wave_profile' && target.wave_profile_id) {
      blocked.add(`wave-directive-profile-${target.wave_profile_id}`)
    }

    if (target.target_type === 'wave_directive' && target.parent_profile_id) {
      blocked.add(buildWaveScheduleTargetId('wave_profile', target.parent_profile_id))
    }
  })

  return blocked
}

export const targetDisabled = (option: WaveScheduleTargetOption, selectedTargets: WaveScheduleTarget[]) => {
  const blocked = targetConflictIds(selectedTargets)
  if (blocked.has(option.id)) return true
  if (option.target_type === 'wave_directive' && option.parent_profile_id) {
    return blocked.has(`wave-directive-profile-${option.parent_profile_id}`)
  }
  return false
}

export const createDraftFromSchedule = (
  schedule: WaveSchedule | null,
  timezone: string,
  fallbackTargets: WaveScheduleTarget[] = []
): WaveScheduleDraft => {
  const effectiveTimezone = schedule?.warehouse_timezone || timezone
  const defaultStartDate = moment.tz(effectiveTimezone).format('YYYY-MM-DD')

  return {
    id: schedule?.id,
    name: schedule?.name || '',
    is_active: schedule?.is_active ?? true,
    schedule_type: schedule?.schedule_type || 'daily',
    recurrence_interval: schedule?.recurrence_interval || 1,
    execution_times: schedule?.execution_times?.length ? schedule.execution_times : ['06:30:00'],
    days_of_week: schedule?.days_of_week || [1, 2, 3, 4, 5],
    date_rule: schedule?.date_rule || 'day_of_month',
    day_of_month: schedule?.day_of_month || 1,
    week_segment: schedule?.week_segment || 'first',
    day_of_week: schedule?.day_of_week || 1,
    targets: schedule?.targets?.length ? schedule.targets : fallbackTargets,
    blackout_dates: schedule?.blackout_dates || [],
    warehouse_timezone: effectiveTimezone,
    start_date: schedule?.start_date || defaultStartDate,
    end_date: schedule?.end_date || null,
  }
}

export const createScheduleFromDraft = (draft: WaveScheduleDraft, existingId?: number): WaveSchedule => {
  const nextOccurrence = getNextRunIso(draft)
  const startDate = draft.start_date || getTodayDateInTimezone(draft.warehouse_timezone)
  return {
    id: existingId || Date.now(),
    name: draft.name,
    is_active: draft.is_active,
    schedule_type: draft.schedule_type,
    recurrence_interval: draft.recurrence_interval,
    execution_times: draft.execution_times.slice().sort(),
    days_of_week: draft.schedule_type === 'weekly' ? draft.days_of_week : null,
    date_rule: draft.schedule_type === 'monthly' ? draft.date_rule : null,
    day_of_month: draft.schedule_type === 'monthly' && draft.date_rule === 'day_of_month' ? draft.day_of_month : null,
    week_segment: draft.schedule_type === 'monthly' && draft.date_rule === 'month_segment' ? draft.week_segment : null,
    day_of_week: draft.schedule_type === 'monthly' && draft.date_rule === 'month_segment' ? draft.day_of_week : null,
    targets: draft.targets.map((target, index) => ({ ...target, execution_order: index + 1 })),
    blackout_dates: draft.blackout_dates,
    start_date: startDate,
    end_date: draft.end_date,
    next_occurrence_local: draft.is_active && nextOccurrence ? nextOccurrence : null,
    next_occurrence_at: null,
    last_run_at: null,
    last_launch_status: 'pending',
    last_downstream_run: null,
    warehouse_timezone: draft.warehouse_timezone,
    calendar_entries: [],
  }
}

export const formatDownstreamStatus = (run?: WaveSchedule['last_downstream_run']) => {
  if (!run) return 'No result yet'
  if (run.status === 'queued') return 'Queued'
  if (run.status === 'running') return 'In progress'
  if (run.status === 'completed') return 'Completed'
  return 'Failed'
}
